/**
 * Out-of-frame call window for in-app meetings.
 *
 * WHY: when the app is embedded in Microsoft Fabric it renders two cross-origin
 * iframes deep, and the portal's outer iframe is
 * `<iframe allow="clipboard-read; clipboard-write">` — `microphone` is not
 * delegated. Permissions Policy is inherited down the frame tree, so the nested
 * board frame can never obtain the mic no matter what its own `allow` says, and
 * the user is never even prompted. The in-frame call therefore connects and is
 * permanently deaf (see `@/lib/micDiagnostics`).
 *
 * A popup is a top-level browsing context: it does not inherit the embedder's
 * Permissions Policy, so it gets the default `microphone: self` and prompts
 * normally. Verified on dev inside Fabric — `getUserMedia` in a popup opened from
 * the board frame resolved with a real device while the same call failed in-frame.
 *
 * The standalone `/meeting?token=` route needs no Keycloak session (the meeting
 * token is the credential, and `/v1/meeting/offer` + `/v1/meeting/{token}/events`
 * both take it as a query param), so the popup needs no auth bridge.
 *
 * Module-level singleton rather than React state because two unrelated components
 * need the same handle: `LLMCallDropdown` opens it during the click, and
 * `InAppMeetingPanel` later focuses/closes it.
 */

import { withCallHostFlags } from './meetingSession'

const CALL_WINDOW_NAME = 'intuigence-call'
const CALL_WINDOW_WIDTH = 380
const CLOSE_POLL_MS = 1000
/** Gap between the docked call window and the board's call panel, in screen px. */
const DOCK_GAP = 12
/**
 * Height of a popup's own title and address bars. `window.open` sizes the content
 * area, so this is subtracted for the first paint; `fitToPanel` then sets the real
 * outer height once the window exists. Measured 66 on Chrome for macOS.
 */
const POPUP_CHROME_HEIGHT = 66

let callWindow: Window | null = null
let callWindowFeatures = `width=${CALL_WINDOW_WIDTH},height=620`
/** The panel height the docked window is fitted to, kept for a replacement window. */
let dockHeight: number | null = null

/** Where the board's call panel is, in this frame's viewport. */
export interface PanelRect {
  x: number
  y: number
  width: number
  height: number
}

/** Where this frame's viewport starts on screen. */
export interface ScreenPoint {
  x: number
  y: number
}

/** The window metrics `dockedWindowFeatures` reads; injectable for tests. */
export type ScreenMetrics = Pick<
  Window,
  'screenX' | 'screenY' | 'outerWidth' | 'outerHeight' | 'innerWidth' | 'innerHeight'
>

let frameOrigin: ScreenPoint | null = null

/**
 * Record where this frame's viewport sits on screen, from the pointer press that
 * starts a call. A cross-origin frame has no API for its own position, but a
 * pointer event carries both coordinates: `screen - client` is the frame's origin,
 * however Fabric lays the iframe out. A keyboard-made event has no position (all
 * zeros) and clears the origin. The origin is used by the next reservation only.
 */
export function rememberFrameOrigin(event: Pick<MouseEvent, 'screenX' | 'screenY' | 'clientX' | 'clientY'>): void {
  // A positionless event drops any earlier origin: the window may have moved since.
  frameOrigin =
    event.screenX || event.screenY
      ? { x: event.screenX - event.clientX, y: event.screenY - event.clientY }
      : null
}

/**
 * `window.open` features that put the call window directly left of the board's
 * call panel (right of it when the browser window has no room on the left),
 * top-aligned with it, so the two read as one unit.
 *
 * Positions come from `origin` (see `rememberFrameOrigin`). Without one, this
 * assumes the frame's viewport reaches the window's right and bottom edges, which
 * holds for the top-level app and roughly for Fabric.
 */
export function dockedWindowFeatures(
  panel: PanelRect,
  metrics: ScreenMetrics = window,
  origin: ScreenPoint | null = frameOrigin
): string {
  const height = Math.max(panel.height - POPUP_CHROME_HEIGHT, 200)
  const frameLeft = origin?.x ?? metrics.screenX + metrics.outerWidth - metrics.innerWidth
  const frameTop = origin?.y ?? metrics.screenY + metrics.outerHeight - metrics.innerHeight
  const leftOfPanel = frameLeft + panel.x - DOCK_GAP - CALL_WINDOW_WIDTH
  // A panel dragged near the frame's left edge leaves no room there; dock on its right instead.
  const left = Math.round(
    leftOfPanel >= metrics.screenX ? leftOfPanel : frameLeft + panel.x + panel.width + DOCK_GAP
  )
  const top = Math.round(frameTop + panel.y)
  return `width=${CALL_WINDOW_WIDTH},height=${height},left=${left},top=${top}`
}

/**
 * Make the whole window, title and address bars included, exactly as tall as the
 * panel. `window.open` can only size the content area, and the bars' height
 * differs by OS and browser; the opener may resize a window it opened.
 */
function fitToPanel(target: Window, panelHeight: number): void {
  try {
    target.resizeTo(target.outerWidth, panelHeight)
  } catch {
    // Cosmetic: the window keeps its estimated height.
  }
}

const PLACEHOLDER_BODY_CSS = [
  'margin:0',
  'height:100vh',
  'display:flex',
  'align-items:center',
  'justify-content:center',
  'background:#111827',
  'color:#e5e7eb',
  'font:15px/1.5 system-ui,sans-serif',
].join(';')

/**
 * Minimal placeholder shown between the click and the token arriving. Booting the
 * real app here instead would flash a "Disconnected" call UI and log a missing-token
 * error, because `/meeting` with no token renders a dead session.
 */
function paintPlaceholder(target: Window, label: string): void {
  const doc = target.document
  doc.title = label
  if (!doc.body) return
  doc.body.style.cssText = PLACEHOLDER_BODY_CSS
  doc.body.textContent = label
}

/**
 * Open the call window NOW, before any await.
 *
 * Must be called synchronously from the click handler: transient user activation
 * does not survive the workspace lookup plus the `start-direct` round-trip, and a
 * `window.open` after those awaits is silently popup-blocked some of the time —
 * an intermittent failure is worse than a deterministic one.
 *
 * @param dockTo the board's call panel; the window opens docked to its left.
 * @returns false when the popup was blocked; the caller should surface that.
 */
export function reserveCallWindow(dockTo?: PanelRect): boolean {
  // Reuse a window that is already open rather than orphaning it.
  if (callWindow && !callWindow.closed) {
    callWindow.focus()
    return true
  }

  if (dockTo) {
    callWindowFeatures = dockedWindowFeatures(dockTo)
    dockHeight = dockTo.height
  }
  // One press, one call: a later call must not reuse a position from before the window moved.
  frameOrigin = null
  const opened = window.open('', CALL_WINDOW_NAME, callWindowFeatures)
  if (!opened) {
    callWindow = null
    return false
  }

  callWindow = opened
  if (dockHeight) fitToPanel(opened, dockHeight)
  try {
    paintPlaceholder(opened, 'Connecting…')
  } catch {
    // about:blank normally inherits the opener's origin, but not if a sandbox
    // stripped `allow-same-origin`. Cosmetic only — the navigation below still
    // works, so don't fail the call over it.
  }
  return true
}

/**
 * URL of the standalone call page for a meeting token.
 *
 * The `interactive` flag is load-bearing, not decoration: the same `/meeting`
 * route is what Recall/Attendee render as the bot's camera feed inside a
 * customer's meeting, and it must not run mic diagnostics there. See
 * `@/lib/meetingSession`.
 */
export function callWindowUrl(token: string): string {
  return withCallHostFlags(
    `${window.location.origin}/meeting?token=${encodeURIComponent(token)}`
  )
}

/**
 * Point the reserved window at the live call. Falls back to opening it here if
 * the reservation was lost (window closed between click and token).
 */
export function launchCall(token: string): boolean {
  const url = callWindowUrl(token)

  if (!callWindow || callWindow.closed) {
    callWindow = window.open(url, CALL_WINDOW_NAME, callWindowFeatures)
    if (callWindow && dockHeight) fitToPanel(callWindow, dockHeight)
    return !!callWindow
  }

  callWindow.location.replace(url)
  callWindow.focus()
  return true
}

/** Handle to the popup, for the panel's direct `postMessage` path. */
export function getCallWindow(): Window | null {
  return callWindow && !callWindow.closed ? callWindow : null
}

export function isCallWindowOpen(): boolean {
  return !!callWindow && !callWindow.closed
}

/** Bring an already-running call back to the front. */
export function focusCallWindow(): boolean {
  if (!isCallWindowOpen()) return false
  callWindow?.focus()
  return true
}

/**
 * Close the call window. Clears the handle first so a watcher started by
 * `onCallWindowClosed` treats this as an intentional teardown and does NOT fire
 * its callback — otherwise ending the call from the panel would re-enter
 * `endInAppMeeting` via the watcher.
 */
export function closeCallWindow(): void {
  const w = callWindow
  callWindow = null
  if (w && !w.closed) {
    w.close()
  }
}

/**
 * Invoke `onClosed` once if the user closes the call window themselves, so the
 * session is torn down instead of orphaned server-side.
 *
 * Polling rather than an event because there is no cross-window "closed" event,
 * and `beforeunload` in the popup is unreliable.
 */
export function onCallWindowClosed(onClosed: () => void): () => void {
  const id = window.setInterval(() => {
    // Handle cleared => closeCallWindow() did it deliberately; stop quietly.
    if (!callWindow) {
      window.clearInterval(id)
      return
    }
    if (callWindow.closed) {
      callWindow = null
      window.clearInterval(id)
      onClosed()
    }
  }, CLOSE_POLL_MS)

  return () => window.clearInterval(id)
}

/** Test seam — drops the module-level handle without touching a real window. */
export function __resetCallWindowForTests(): void {
  callWindow = null
  callWindowFeatures = `width=${CALL_WINDOW_WIDTH},height=620`
  dockHeight = null
  frameOrigin = null
}
